import {
  Alert,
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import {useMemo} from 'react';
import toast from 'react-hot-toast';

import {RqGetAllFinalProducts} from '../../../../../reactQuery/apis/rqFinalProductAPI';
import {RqGetAvailableBatches, RqRegisterProduction} from '../../../../../reactQuery/apis/rqProductionAPI';

const {Text} = Typography;

const PRIMARY = '#4f46e5';
const PRIMARY_LIGHT = '#eef2ff';

const SectionTitle = ({children}) => (
  <div className={'flex items-center gap-2 mb-3'}>
    <div className={'w-1 h-4 rounded-full'} style={{background: PRIMARY}} />
    <Text className={'font-bold text-gray-700 uppercase tracking-widest text-xs'}>
      {children}
    </Text>
  </div>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
};

export const BAG_WEIGHT_OPTIONS = [
  {label: '5.0 KG Bag', value: 5.0},
  {label: '10.0 KG Bag', value: 10.0},
  {label: '20.0 KG Bag', value: 20.0},
  {label: '25.0 KG Bag', value: 25.0},
  {label: '50.0 KG Drum', value: 50.0},
];

const RegisterProductionScreen = ({
  isModalVisible,
  handleCancel,
  form,
  token,
  onProductionRegistered,
}) => {
  const registerProductionMutation = RqRegisterProduction();
  
  const {data: availableBatches = [], isLoading: batchesLoading} = RqGetAvailableBatches(token);
 
  const {data: finalProducts = []} = RqGetAllFinalProducts(token);

  const finalProductOptions = useMemo(
    () =>
      finalProducts.map(p => {
        const name = p.productName || p.name || '';
        return {
          label: p.brand ? `${name} (${p.brand})` : name,
          value: name,
          product: p,
        };
      }),
    [finalProducts],
  );

  const batchOptions = useMemo(
    () =>
      availableBatches.map(b => ({
        label: `${b.batchNumber} - ${b.batchName} (Remaining: ${b.remainingQuantity ?? 0} KG)`,
        value: b.batchNumber || b.id,
        batchNumber: b.batchNumber || b.id,
        remainingQuantity: b.remainingQuantity ?? 0,
      })),
    [availableBatches],
  );

  /* Live watch batch selection and packaging values for validation */
  const selectedBatchIds = Form.useWatch('selectedBatchIds', form) || [];
  const bagWeight = Form.useWatch('bagWeight', form) || 0;
  const bagsCount = Form.useWatch('bags', form) || 0;

  const totalAvailableYield = useMemo(() => {
    return selectedBatchIds.reduce((sum, id) => {
      const match = batchOptions.find(o => o.value === id);
      return sum + (match?.remainingQuantity || 0);
    }, 0);
  }, [selectedBatchIds, batchOptions]);

  const totalPackagedWeight = useMemo(() => {
    return (bagWeight || 0) * (bagsCount || 0);
  }, [bagWeight, bagsCount]);

  const isExceeding = totalPackagedWeight > totalAvailableYield && totalAvailableYield > 0;

  const onFinish = async values => {
    try {
      if (isExceeding) {
        toast.error('Packaged weight exceeds selected bulk batch available stock!');
        return;
      }

      const batches = (values.selectedBatchIds || []).map(id => ({
        batchId: id,
      }));

      const packaging = [
        {
          finalProductName: values.finalProductName,
          brand: values.brand || 'Samadhan Colors',
          category: values.category || 'Natural Food Colors',
          hsnCode: values.hsnCode || '32030010',
          bagWeight: values.bagWeight,
          bags: values.bags,
        },
      ];

      const payload = {
        productionDate: values.productionDate ? values.productionDate.valueOf() : Date.now(),
        batches,
        packaging,
      };

      await registerProductionMutation.mutateAsync({payload, token});
      toast.success('Stage 2 Production Packaging completed successfully!');

      if (onProductionRegistered) {onProductionRegistered(payload);}

      form.resetFields();
      handleCancel();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || 'Failed to register packaging production',
      );
    }
  };

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3 py-1'}>
          <div
            className={'w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold'}
            style={{background: 'linear-gradient(135deg, #4f46e5, #3730a3)'}}
          >
            📦
          </div>
          <div>
            <div className={'font-bold text-gray-800 text-base'}>Stage 2: Final Production Packaging</div>
            <div className={'text-gray-400 font-normal text-xs'}>
              Package bulk batch yield into finished goods & deduct empty packaging bags
            </div>
          </div>
        </div>
      )}
      open={isModalVisible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={'Register Packaging'}
      okButtonProps={{
        className: 'btn-primary px-6',
        disabled: isExceeding,
        loading: registerProductionMutation.isPending || registerProductionMutation.isLoading,
      }}
      cancelButtonProps={{className: 'rounded-md'}}
      width={850}
      destroyOnClose
      styles={{
        header: {borderBottom: `3px solid ${PRIMARY_LIGHT}`, paddingBottom: 12},
        body: {padding: '16px 20px', maxHeight: '78vh', overflowY: 'auto'},
      }}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={onFinish}
        initialValues={{
          bagWeight: 10.0,
          bags: 1,
          brand: 'Samadhan Colors',
          category: 'Natural Food Colors',
          hsnCode: '32030010',
        }}
      >
        {/* Step 1: Select Available Bulk Batches */}
        <div className={'rounded-md p-4 mb-4 bg-indigo-50/50 border border-indigo-100'}>
          <SectionTitle>Step 1: Select Bulk Production Batch(es)</SectionTitle>
          <Row gutter={12}>
            <Col span={16}>
              <Form.Item
                name={'selectedBatchIds'}
                label={<span className={'font-semibold text-slate-600'}>Available Bulk Batches</span>}
                rules={[{required: true, message: 'Select at least one available bulk batch'}]}
              >
                <Select
                  mode={'multiple'}
                  showSearch
                  loading={batchesLoading}
                  options={batchOptions}
                  placeholder={'Select bulk batch (e.g. BATCH-Y101)…'}
                  optionFilterProp={'label'}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={'productionDate'}
                label={<span className={'font-semibold text-slate-600'}>Packaging Date</span>}
                rules={[{required: true, message: 'Packaging date is required'}]}
              >
                <DatePicker
                  className={'w-full'}
                  showTime
                  format={'YYYY-MM-DD HH:mm'}
                  placeholder={'Date & time'}
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedBatchIds.length > 0 && (
            <div className={'p-2.5 bg-white rounded border border-indigo-100 flex items-center justify-between text-xs'}>
              <span className={'text-gray-600 font-medium'}>
                Selected Batches: <strong className={'text-indigo-700'}>{selectedBatchIds.length}</strong>
              </span>
              <span className={'text-gray-600 font-medium'}>
                Available Bulk Yield: <strong className={'text-emerald-600 text-sm'}>{totalAvailableYield} KG</strong>
              </span>
            </div>
          )}
        </div>

        {/* Step 2: Finished Product Packaging Details */}
        <div className={'rounded-md p-4 mb-4 border border-gray-200 bg-white'}>
          <SectionTitle>Step 2: Finished Goods Packaging Specifications</SectionTitle>

          <Row gutter={12}>
            <Col span={14}>
              <Form.Item
                name={'finalProductName'}
                label={<span className={'font-semibold text-slate-600'}>Final Product Name</span>}
                extra={<span className={'text-[11px] text-gray-400'}>Select existing product from dropdown or type new name to create!</span>}
                rules={[{required: true, message: 'Final product name is required'}]}
              >
                <AutoComplete
                  options={finalProductOptions}
                  placeholder={'Select existing final product or enter new name...'}
                  filterOption={(inputValue, option) =>
                    (option?.value || '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                  onSelect={(value, option) => {
                    if (option?.product) {
                      const p = option.product;
                      const update = {
                        finalProductName: p.productName || p.name || value,
                      };
                      if (p.brand) {update.brand = p.brand;}
                      if (p.category) {update.category = p.category;}
                      if (p.hsnCode) {update.hsnCode = p.hsnCode;}
                      if (p.bagWeight !== undefined && p.bagWeight !== null) {update.bagWeight = Number(p.bagWeight);}
                      form.setFieldsValue(update);
                    }
                  }}
                >
                  <Input placeholder={'e.g. Natural Yellow Color 10kg Pack'} />
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name={'brand'}
                label={<span className={'font-semibold text-slate-600'}>Brand Name</span>}
              >
                <Input placeholder={'e.g. Samadhan Colors'} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name={'category'}
                label={<span className={'font-semibold text-slate-600'}>Category</span>}
              >
                <Input placeholder={'e.g. Natural Food Colors'} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={'hsnCode'}
                label={<span className={'font-semibold text-slate-600'}>HSN Code</span>}
              >
                <Input placeholder={'e.g. 32030010'} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={'bagWeight'}
                label={<span className={'font-semibold text-slate-600'}>Bag Weight (Capacity)</span>}
                rules={[{required: true, message: 'Select bag weight'}]}
              >
                <Select options={BAG_WEIGHT_OPTIONS} placeholder={'Select bag capacity'} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={'bags'}
                label={<span className={'font-semibold text-slate-600'}>Number of Bags / Packs</span>}
                rules={[
                  {required: true, message: 'Bag count is required'},
                  {type: 'number', min: 0.01, message: 'Must be greater than 0'},
                ]}
              >
                <InputNumber min={0.01} step={0.01} className={'w-full'} placeholder={'e.g. 50'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <div className={'pt-6 text-right'}>
                <Text className={'text-xs text-gray-500 block'}>Total Packaged Weight</Text>
                <Text className={`text-lg font-bold ${isExceeding ? 'text-red-600' : 'text-indigo-600'}`}>
                  {totalPackagedWeight} KG
                </Text>
              </div>
            </Col>
          </Row>

          {isExceeding && (
            <Alert
              type={'error'}
              showIcon
              message={'Weight Exceeds Available Yield'}
              description={`Packaged weight (${totalPackagedWeight} KG) exceeds total bulk batch yield (${totalAvailableYield} KG). Please adjust bag count or select additional bulk batches.`}
              className={'mt-2'}
            />
          )}
        </div>
      </Form>
    </Modal>
  );
};

RegisterProductionScreen.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  onProductionRegistered: PropTypes.func,
};

RegisterProductionScreen.defaultProps = {
  onProductionRegistered: null,
};

export default RegisterProductionScreen;
